import React from 'react';
import { Card } from '@mui/material';
import * as m from 'motion/react-m';

import { spring } from '../../theme/motion';
import { usePrefersReduced } from './useMotionPreference';

/**
 * Motion-enabled MUI Card.
 *
 * IMPORT CONVENTION (see `index.ts`): the app shell wraps everything in
 * `<LazyMotion strict>`, so the MUI wrapper MUST be created at MODULE SCOPE
 * with `m.create(...)` (the lightweight `m` namespace), never the full
 * `motion.create(...)` which throws under `strict`.
 */
const MotionMuiCard = m.create(Card);

/**
 * Props accepted by `<MotionCard>` — the props of the motion-wrapped Card. We
 * derive them from the created component rather than from MUI's `CardProps`
 * because Motion overrides a few DOM event handlers (`onDrag`, `onAnimationStart`,
 * …) with its own gesture signatures, which clash with MUI's HTML-attribute
 * typings on a raw `CardProps` spread.
 */
export type MotionCardProps = React.ComponentProps<typeof MotionMuiCard>;

/**
 * A drop-in replacement for MUI `<Card>` that adds a crisp spring
 * hover/tap micro-interaction (scale 1.02 / 0.98 via `spring.snappy`).
 *
 * Reduced-motion branch: when `usePrefersReduced()` is true, the
 * `whileHover`/`whileTap` transform props are omitted entirely so the card is
 * fully static (no movement) — all other props pass straight through to the
 * underlying MUI Card.
 */
export const MotionCard: React.FC<MotionCardProps> = ({ children, ...rest }) => {
  const prefersReduced = usePrefersReduced();

  const motionProps = prefersReduced
    ? {}
    : {
        whileHover: { scale: 1.02 },
        whileTap: { scale: 0.98 },
        transition: spring.snappy,
      };

  return (
    <MotionMuiCard {...motionProps} {...rest}>
      {children}
    </MotionMuiCard>
  );
};

export default MotionCard;
